import type { Collaborator } from "@nlc/shared-types";
import type { Path, UseFormReturn } from "react-hook-form";

import { useEffect } from "react";
import { Controller, type FieldValues, useWatch } from "react-hook-form";
import { useListData } from "react-stately";

import { Avatar } from "@/components/avatar";
import { Description, Label } from "@/components/field";
import {
  MultiSelect,
  MultiSelectField,
  MultiSelectItem,
} from "@/components/multi-select";
import { Select, SelectButton } from "@/components/select";
import { Tag } from "@/components/tag-group";
import { useCollaboratorPermissions } from "@/features/request/shared/useCollaboratorPermissions";
import { useGetProjectCollaborators } from "@/features/request/shared/UseGetProjectCollaborators.tsx";

export function CollaboratorsSelect<T extends FieldValues>({
  brandFieldName = "brand" as Path<T>,
  fieldName = "collaboratorIds" as Path<T>,
  methods,
}: {
  brandFieldName?: Path<T>;
  fieldName?: Path<T>;
  methods: UseFormReturn<T>;
}) {
  const { data } = useGetProjectCollaborators();
  const rawBrandId = useWatch({
    control: methods.control,
    name: brandFieldName,
  });
  const parsedBrandId = Number(rawBrandId);
  const brandId = parsedBrandId > 0 ? parsedBrandId : null;
  const { canEdit, canManage } = useCollaboratorPermissions(brandId);

  const defaultItems = methods.getValues(fieldName);

  const selectedList = useListData<Collaborator>({
    getKey: (item) => item.id,
    initialItems: [],
  });

  useEffect(() => {
    if (data) {
      selectedList.remove(...selectedList.items.map((item) => item.id));
      selectedList.append(
        ...data.filter((item) => defaultItems.includes(item.id)),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, defaultItems]);

  if (data) {
    return (
      <Controller
        control={methods.control}
        name={fieldName}
        render={({ field: { name, onBlur, onChange, value } }) => (
          <MultiSelectField className="col-span-6">
            <Label>Collaborators</Label>
            <MultiSelect
              isDisabled={!canEdit}
              isItemRemovable={canManage}
              items={data.filter((item) => canManage(item))}
              key={brandId ?? "no-brand"}
              name={name}
              onBlur={onBlur}
              onItemAdd={(key) => {
                const newValue = [...(value || []), key];

                onChange(newValue);
              }}
              onItemRemove={(key) => {
                const newValue = (value || []).filter(
                  (id: number) => id !== key,
                );

                onChange(newValue);
              }}
              placeholder="Select"
              renderEmptyState={(inputValue) => (
                <span className="p-2">
                  {inputValue ? (
                    <>No results found for: {inputValue}</>
                  ) : (
                    `No collaborators available`
                  )}
                </span>
              )}
              selectedList={selectedList}
              tag={(item) => (
                <Tag isDisabled={!canManage(item)} textValue={String(item.id)}>
                  <Avatar
                    alt={item.textValue}
                    className="rounded-full size-3"
                    src={item.profilePicture}
                  />
                  {item.textValue}
                </Tag>
              )}
            >
              {(item) => (
                <MultiSelectItem id={item.id}>{item.textValue}</MultiSelectItem>
              )}
            </MultiSelect>
            <Description>
              Adding a collaborator will give them access to view project
              details, uploaded project files and comments between our team and
              yours. Please make sure you are only adding collaborators who you
              want to access this project fully.
            </Description>
          </MultiSelectField>
        )}
      />
    );
  }

  return (
    <Select className="col-span-6" placeholder="Loading...">
      <Label>Collaborators</Label>
      <SelectButton />
      <Description>
        Adding a collaborator will give them access to view project details,
        uploaded project files and comments between our team and yours. Please
        make sure you are only adding collaborators who you want to access this
        project fully.
      </Description>
    </Select>
  );
}
